import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import Container from "components/core/Page/Container";
import SectionHeader from "components/molecules/SectionHeader";
import { NearbyCityRow } from "components/molecules";
import storage from "assets/icons/psp/storage-near-you.svg";
import { getDistance } from "utils/helpers";
import {
	nearbyCitiesSample,
	stateWiseCities,
	storageNearYouStates
} from "./StorageNearYouData";

import "./StorageNearYou.scss";

const StorageNearYou = ({
	pageType = "PSP",
	background = "white",
	sectionTitle,
	stateTitlePrefix,
	countryTitle: countryTitleProp,
	facilityLocation
}) => {
	const userGeolocation = useSelector((state) => state.user?.geolocation);
	const nearbyCitiesFromRedux = useSelector(
		(state) => state.app?.nearbyCities
	);

	const geolocation = facilityLocation
		? {
				city: facilityLocation.city,
				region: facilityLocation.state || facilityLocation.fullState,
				latitude: facilityLocation.latitude,
				longitude: facilityLocation.longitude
			}
		: userGeolocation;

	const cityName = geolocation?.city || "Your City";
	const stateName = geolocation?.region || "Your State";

	const withDistance = (item) => ({
		...item,
		distance: geolocation
			? getDistance(
					Number(geolocation.latitude),
					Number(geolocation.longitude),
					Number(item.latitude),
					Number(item.longitude)
				)
			: null
	});

	const citiesByState =
		stateWiseCities.find(
			(item) =>
				item.abbreviation === stateName || item.state === stateName
		) || stateWiseCities[0];

	const nearbyCities = facilityLocation
		? (citiesByState?.cities || [])
				.map((item) => ({
					...withDistance(item),
					content_id: item.name,
					city: item.name,
					state: citiesByState?.abbreviation || stateName,
					uri: item.uri
				}))
				.filter((item) => item.distance !== 0)
				.sort((a, b) => (a.distance || 0) - (b.distance || 0))
				.slice(0, 6)
		: (Array.isArray(nearbyCitiesFromRedux) && nearbyCitiesFromRedux.length
				? nearbyCitiesFromRedux.map((c) => ({
						city: c.city || c.name,
						state: c.state || "",
						uri: c.uri,
						content_id: c.content_id || c.name,
						distance: c.distance
					}))
				: nearbyCitiesSample
		  ).slice(0, 6);

	const stateAbbreviation = citiesByState?.abbreviation || stateName;

	const popularCitiesByState = (citiesByState?.cities || [])
		.map((item) => ({
			...withDistance(item),
			content_id: item.name,
			city: item.name,
			state: stateAbbreviation,
			uri: item.uri
		}))
		.filter((item) => item.distance !== 0)
		.slice(0, 6);

	const popularCities = storageNearYouStates.cities
		.map((item) => {
			const [city, state] = item.name.split(", ");
			return {
				...withDistance(item),
				content_id: item.name,
				city,
				state,
				uri: item.uri
			};
		})
		.filter((item) => item.distance !== 0)
		.slice(0, 6);

	const cityTitle = `Storage Units Near ${cityName}, ${stateAbbreviation}`;
	const stateTitle = stateTitlePrefix
		? `${stateTitlePrefix} ${citiesByState.state}`
		: `Storage Units in ${citiesByState.state}`;
	const countryTitle = countryTitleProp || `Storage Units Across the Country`;

	return (
		<section
			className={`storage-near-you-section storage-near-you-section--${background}`}
		>
			<Container className="storage-near-you-container">
				<SectionHeader
					title={sectionTitle || "Find Storage Near You"}
					contentAlign="text-center"
					additionalProps={{
						paragraph: {
							size: "standard",
							color: "black",
							weight: "regular"
						}
					}}
					titleProps={{
						size: "xxlarge",
						weight: "bold",
						color: "black"
					}}
					className={`${pageType.toLowerCase()}-header`}
					image={storage}
				/>
				<div className="storage-near-you-columns">
					<NearbyCityRow
						rowTitle={cityTitle}
						nearbyCities={nearbyCities}
					/>
					<NearbyCityRow
						rowTitle={stateTitle}
						nearbyCities={popularCitiesByState}
					/>
					<NearbyCityRow
						rowTitle={countryTitle}
						nearbyCities={popularCities}
					/>
				</div>
			</Container>
		</section>
	);
};

StorageNearYou.propTypes = {
	pageType: PropTypes.string,
	background: PropTypes.oneOf(["white", "light-blue"]),
	sectionTitle: PropTypes.string,
	stateTitlePrefix: PropTypes.string,
	countryTitle: PropTypes.string,
	facilityLocation: PropTypes.shape({
		city: PropTypes.string,
		state: PropTypes.string,
		fullState: PropTypes.string,
		latitude: PropTypes.number,
		longitude: PropTypes.number
	})
};

export default StorageNearYou;
